import { api } from '@/api/client';
import EventBus from '@/utils/eventBus';
import { envStorage } from '@/utils/envCache';
import type {LoginCredentials, Teams, Tokens, UserInfo} from "@/types";


// 身份验证服务
export class AuthService {
    static getAccessToken(): string | null {
        return envStorage.getItem('accessToken');
    }

    static getRefreshToken(): string | null {
        return envStorage.getItem('refreshToken');
    }

    static setTokens({ accessToken, refreshToken }: Tokens): void {
        envStorage.setItem('accessToken', accessToken);
        if (refreshToken) {
            envStorage.setItem('refreshToken', refreshToken);
        }
    }

    static setUserInfo(userInfo: UserInfo): void {
        envStorage.setItem('userInfo', JSON.stringify(userInfo));
        if (userInfo.teamId !== undefined && userInfo.teamId !== null) {
            envStorage.setItem('teamId', String(userInfo.teamId));
        }
    }
    static getUserInfo(): UserInfo | null {
        const userInfo = envStorage.getItem('userInfo');
    
        return userInfo ? JSON.parse(userInfo) : null;
    }

    static setTeamId(teamId: string): void {
        return envStorage.getItem('teamId');
    }



    static clearTokens(): void {
        envStorage.removeItem('accessToken');
        envStorage.removeItem('refreshToken');
        envStorage.removeItem('admin');
        envStorage.removeItem('isUploading')
        envStorage.removeItem("isFULL_CONTROL")
        envStorage.removeItem('teamId');
    }

    static isAuthenticated(): boolean {
        return !!this.getAccessToken();
    }



    static async login(credentials: LoginCredentials): Promise<UserInfo> {
        try {
            const response = await api.post<UserInfo>('/v1/user/login', credentials,{
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                }
            });
            if (response) {
                this.setTokens({
                    accessToken: response.token,
                    refreshToken: ''
                });

                return response;
            }

            return response;

        } catch (error) {
            console.error('登录失败:', error);
            throw error;
        }
    }

    static async teamQuery(email:string): Promise<Teams[]>{
        try {
            const response = await api.get<Teams[]>('/v1/user/team/query', {
                params: {
                    email
                }
            });
            if (response) {
                return response;
            }
            return [];
        } catch (error) {
            console.error('获取团队失败:', error);
            throw error;
        }
    }

    static async logout(): Promise<void> {
        try {
            // 调用服务端注销
            await api.post('/auth/logout', {
                refreshToken: this.getRefreshToken(),
            });
        } catch (error) {
            console.error('注销时出错:', error);
        } finally {
            // 清除token
            this.clearTokens();
            // 通知应用
            EventBus.emit('authChange');
        }
    }

    static async refreshToken(): Promise<string> {
        const refreshToken = this.getRefreshToken();

        if (!refreshToken) {
            throw new Error('刷新令牌不存在');
        }

        try {
            const response = await api.post<{ accessToken: string; refreshToken?: string }>(
                '/auth/refresh-token',
                { refreshToken }
            );

            if (response.accessToken) {
                this.setTokens({
                    accessToken: response.accessToken,
                    refreshToken: response.refreshToken || refreshToken,
                });
                return response.accessToken;
            }

            throw new Error('刷新令牌失败');

        } catch (error) {
            console.error('刷新令牌失败:', error);
            // 刷新失败时触发全局注销
            EventBus.emit('sessionExpired');
            this.clearTokens();
            throw error;
        }
    }

    static getAuthHeader(): Record<string, string> {
        const token = this.getAccessToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    /**
     * 获取并更新用户信息
     */
    static async fetchAndUpdateUserInfo(): Promise<UserInfo | null> {
        try {
            const { userApi } = await import('@/api/aiApi');
            const response = await userApi.getUserInfo();

            if (response) {
                // 更新本地存储的用户信息
                this.setUserInfo(response as UserInfo);
                return response as UserInfo;
            }

            return null;
        } catch (error) {
            console.error('获取用户信息失败:', error);
            return null;
        }
    }
}

// 全局导出
export default AuthService;
